import { useState, useEffect, useCallback, Fragment } from "react";
import PropTypes from "prop-types";

import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
  useTheme
} from "@mui/material";
import * as Sentry from "@sentry/react";

import { LoadingButton } from "@aclymatepackages/atoms";
import {
  AGREEMENT_NOUN_BY_TIER,
  TIER_STATUS_KEY_BY_TIER,
  TIER_LABEL_BY_TIER
} from "@aclymatepackages/constants";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey } from "@fortawesome/free-solid-svg-icons";

import Card from "../atoms/mui/Card";
import GrayPaper from "../atoms/GrayPaper";
import CopyToClipboardIcon from "../atoms/buttons/CopyToClipboardIcon";
import EulaAcceptanceModal from "./EulaAcceptanceModal";
import ConnectionInstructionsTabs from "./ConnectionInstructionsTabs";

import { fetchOurApi } from "../../helpers/utils/apiCalls";
import { analyticsTrack } from "../../helpers/analytics";

const formatIsoDate = (isoString) => {
  if (!isoString) {
    return null;
  }
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
};

const buildGenerateHelperText = ({
  requiresAcceptance,
  currentVersion,
  agreementNoun
}) => {
  if (!requiresAcceptance) {
    return null;
  }
  if (currentVersion) {
    return `You need to accept the ${agreementNoun} (v${currentVersion}) before generating a key.`;
  }
  return `You need to accept the ${agreementNoun} before generating a key.`;
};

const EulaStatusPill = ({
  requiresAcceptance,
  currentVersion,
  agreementNoun,
  onClick
}) => {
  const clickProps = onClick
    ? { onClick, clickable: true, sx: { cursor: "pointer" } }
    : {};
  if (requiresAcceptance) {
    return (
      <Chip
        size="small"
        color="warning"
        label={`${agreementNoun}: Not accepted`}
        {...clickProps}
      />
    );
  }
  const label = currentVersion
    ? `${agreementNoun}: Accepted (v${currentVersion})`
    : `${agreementNoun}: Accepted`;
  return <Chip size="small" color="success" label={label} {...clickProps} />;
};

const TestModePill = () => (
  <Chip size="small" color="warning" variant="outlined" label="Test" />
);

const KeyRevealPanel = ({ revealedKey, onConfirm }) => {
  const { palette } = useTheme();
  const [hasConfirmedCopy, setHasConfirmedCopy] = useState(false);
  const { rawKey } = revealedKey || {};

  const onToggle = () => setHasConfirmedCopy((prev) => !prev);

  return (
    <GrayPaper
      style={{
        borderLeft: `8px solid ${palette.error.main}`,
        padding: 16
      }}
    >
      <div role="alert" aria-live="assertive">
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <Chip size="small" color="error" label="Copy your key now" />
          <Typography variant="subtitle2" color="error">
            You will not see this key again. Copy it now.
          </Typography>
        </Stack>
        <Box
          sx={{
            bgcolor: palette.common.white,
            borderRadius: 1,
            p: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1
          }}
        >
          <Box
            component="code"
            sx={{ wordBreak: "break-all", fontFamily: "monospace" }}
          >
            {rawKey}
          </Box>
          <CopyToClipboardIcon text={rawKey} />
        </Box>
      </div>
      <Box mt={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={hasConfirmedCopy}
              onChange={onToggle}
              color="primary"
            />
          }
          label="I have copied the key and stored it securely"
        />
      </Box>
      <Box mt={1}>
        <LoadingButton
          label="Confirm"
          color="primary"
          onClick={onConfirm}
          isLoading={false}
          disabled={!hasConfirmedCopy}
        />
      </Box>
    </GrayPaper>
  );
};

// Ops sets rateLimitCallsPerDay to 0 as a per-key kill switch.
const buildRateLimitCopy = (rateLimitCallsPerDay) => {
  if (rateLimitCallsPerDay === 0) {
    return " · disabled (0/day cap)";
  }
  if (!rateLimitCallsPerDay) {
    return null;
  }
  return ` · ${rateLimitCallsPerDay}/day cap`;
};

const ActiveKeyRow = ({
  apiKey,
  isAdmin,
  isMutating,
  pendingAction,
  onRotate,
  onRevoke
}) => {
  const {
    keyId,
    keyMasked,
    mode,
    createdAt,
    createdBy,
    lastUsedAt,
    rateLimitCallsPerDay
  } = apiKey || {};
  const isTestMode = mode === "test";
  const isRotatingThisRow =
    pendingAction &&
    pendingAction.keyId === keyId &&
    pendingAction.action === "rotate";
  const isRevokingThisRow =
    pendingAction &&
    pendingAction.keyId === keyId &&
    pendingAction.action === "revoke";

  return (
    <GrayPaper style={{ padding: 12 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Stack spacing={0.5} flexGrow={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              component="code"
              sx={{ fontFamily: "monospace", fontWeight: 500 }}
            >
              {keyMasked}
            </Box>
            {isTestMode && <TestModePill />}
          </Stack>
          <Typography variant="caption" color="textSecondary">
            {createdAt && `Created ${formatIsoDate(createdAt)}`}
            {isAdmin && createdBy && ` · by ${createdBy}`}
          </Typography>
          {isAdmin && (
            <Typography variant="caption" color="textSecondary">
              {lastUsedAt
                ? `Last used ${formatIsoDate(lastUsedAt)}`
                : "Not used yet"}
              {buildRateLimitCopy(rateLimitCallsPerDay)}
            </Typography>
          )}
        </Stack>
        {isAdmin && (
          <Stack direction="row" spacing={1}>
            <LoadingButton
              label="Rotate"
              color="primary"
              onClick={() => onRotate(keyId)}
              isLoading={isRotatingThisRow}
              disabled={isMutating}
            />
            <LoadingButton
              label="Revoke"
              color="error"
              onClick={() => onRevoke(keyId)}
              isLoading={isRevokingThisRow}
              disabled={isMutating}
            />
          </Stack>
        )}
      </Stack>
    </GrayPaper>
  );
};

const AuditListRow = ({ apiKey }) => {
  const { keyMasked, mode, createdAt, revokedAt, revokedBy, rotatedFromKeyId } =
    apiKey || {};
  const isTestMode = mode === "test";

  return (
    <GrayPaper style={{ padding: 12 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              component="code"
              sx={{ fontFamily: "monospace", fontWeight: 500 }}
            >
              {keyMasked}
            </Box>
            {isTestMode && <TestModePill />}
            {rotatedFromKeyId && (
              <Chip
                size="small"
                color="info"
                variant="outlined"
                label="Rotated"
              />
            )}
          </Stack>
          <Typography variant="caption" color="textSecondary">
            {createdAt && `Created ${formatIsoDate(createdAt)}`}
            {revokedAt && ` · Revoked ${formatIsoDate(revokedAt)}`}
            {revokedBy && ` · by ${revokedBy}`}
          </Typography>
        </Stack>
      </Stack>
    </GrayPaper>
  );
};

const ApiKeyManagement = ({
  tier,
  accountId,
  isAdmin,
  activateSnackbar,
  title,
  analyticsEventPrefix = "api_key",
  sentryFeaturePrefix = "explorer-api-keys",
  extraSlots
}) => {
  const [listState, setListState] = useState({
    keys: [],
    eulaStatus: null,
    error: false,
    errorCode: null,
    loading: true
  });
  const [isMutating, setIsMutating] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [revealedKey, setRevealedKey] = useState(null);
  const [modalMode, setModalMode] = useState(null);

  const agreementNoun = AGREEMENT_NOUN_BY_TIER[tier] || "agreement";
  const tierStatusKey = TIER_STATUS_KEY_BY_TIER[tier] || "tier2";
  const tierLabel = TIER_LABEL_BY_TIER[tier] || tier;

  const refetchList = useCallback(async () => {
    if (!accountId) {
      setListState({
        keys: [],
        eulaStatus: null,
        error: false,
        errorCode: null,
        loading: false
      });
      return;
    }
    setListState((prev) => ({
      ...prev,
      loading: true,
      error: false,
      errorCode: null
    }));
    const queryString = `?companyId=${encodeURIComponent(
      accountId
    )}&tier=${encodeURIComponent(tier)}`;
    const res = await fetchOurApi({
      path: `/api-keys/list${queryString}`,
      method: "GET"
    }).catch((err) => ({ error: err }));
    if (!res || res.error) {
      const errorCode =
        (res && res.error && res.error.code) ||
        (res && res.error && res.error.name) ||
        "unknown";
      Sentry.captureException(
        (res && res.error) || new Error("api-keys list failed"),
        {
          tags: { feature: `${sentryFeaturePrefix}-list` },
          extra: { accountId, tier, errorCode }
        }
      );
      setListState({
        keys: [],
        eulaStatus: null,
        error: true,
        errorCode,
        loading: false
      });
      return;
    }
    setListState({
      keys: res.keys || [],
      eulaStatus: res.eulaStatus || null,
      error: false,
      errorCode: null,
      loading: false
    });
  }, [accountId, tier, sentryFeaturePrefix]);

  useEffect(() => {
    refetchList();
  }, [refetchList]);

  const {
    keys,
    eulaStatus,
    error: listError,
    errorCode: listErrorCode,
    loading: listLoading
  } = listState;

  const AUTH_ERROR_CODES = ["not_authorized", "not_admin"];
  const isAuthError = AUTH_ERROR_CODES.includes(listErrorCode);
  const listErrorCopy = isAuthError
    ? "You don't have permission to view API keys for this company. Ask an admin on this account to grant access."
    : "Couldn't load your API keys — refresh to retry.";
  const activeKeys = keys.filter((keyRow) => !keyRow.revokedAt);
  const revokedKeys = keys.filter((keyRow) => keyRow.revokedAt);

  const tierEulaStatus = eulaStatus ? eulaStatus[tierStatusKey] : null;
  const requiresAcceptance = tierEulaStatus
    ? tierEulaStatus.requiresAcceptance !== false
    : true;
  const currentVersion = tierEulaStatus ? tierEulaStatus.currentVersion : null;

  const hasActiveKey = activeKeys.length > 0;
  // The backend's single-active-key check is per tier and mode, and the UI
  // only mints live keys, so an active test key must not block generation.
  const hasActiveLiveKey = activeKeys.some((keyRow) => keyRow.mode === "live");
  const disableGenerate =
    !isAdmin ||
    listLoading ||
    isMutating ||
    requiresAcceptance ||
    hasActiveLiveKey;
  const generateHelperText = buildGenerateHelperText({
    requiresAcceptance,
    currentVersion,
    agreementNoun
  });

  const surfaceError = (error, feature, fallbackMessage) => {
    Sentry.captureException(error, {
      tags: { feature },
      extra: { accountId, tier }
    });
    const message =
      (error && error.message) || fallbackMessage || "Something went wrong.";
    if (activateSnackbar) {
      activateSnackbar({ severity: "error", message });
      return;
    }
    console.error(`[api-keys] ${feature}:`, message, error);
  };

  const surfaceInfo = (message) => {
    if (activateSnackbar) {
      activateSnackbar({ severity: "info", message });
      return;
    }
    console.info(`[api-keys]`, message);
  };

  const isValidCreateResponse = (res) =>
    res &&
    typeof res.rawKey === "string" &&
    res.rawKey.length > 0 &&
    typeof res.keyId === "string" &&
    res.keyId.length > 0 &&
    typeof res.keyMasked === "string";

  const onGenerate = async () => {
    setIsMutating(true);
    try {
      const res = await fetchOurApi({
        path: "/api-keys/create",
        method: "POST",
        data: { companyId: accountId, tier, label: "" }
      }).catch((err) => ({
        error: { code: "network", message: err && err.message }
      }));
      if (!res || res.error) {
        const errObj = (res && res.error) || { code: "unknown" };
        if (errObj.code === "eula_not_accepted") {
          analyticsTrack(`${analyticsEventPrefix}_generate_blocked_eula`, {
            accountId,
            tier,
            currentVersion: errObj.currentVersion || null
          });
          surfaceInfo(`Accept the ${agreementNoun} before generating a key.`);
          return;
        }
        surfaceError(
          errObj,
          `${sentryFeaturePrefix}-create`,
          "Couldn't generate a key. Please try again."
        );
        return;
      }
      if (!isValidCreateResponse(res)) {
        surfaceError(
          new Error("empty_response_body"),
          `${sentryFeaturePrefix}-create-empty-response`,
          "Key creation returned an unexpected response. Please try again."
        );
        return;
      }
      analyticsTrack(`${analyticsEventPrefix}_generated`, {
        accountId,
        tier
      });
      setRevealedKey({
        rawKey: res.rawKey,
        keyId: res.keyId,
        keyMasked: res.keyMasked
      });
      await refetchList();
    } finally {
      setIsMutating(false);
    }
  };

  const onRotate = async (keyId) => {
    setIsMutating(true);
    setPendingAction({ keyId, action: "rotate" });
    try {
      const res = await fetchOurApi({
        path: "/api-keys/rotate",
        method: "POST",
        data: { companyId: accountId, keyId }
      }).catch((err) => ({
        error: { code: "network", message: err && err.message }
      }));
      if (!res || res.error) {
        surfaceError(
          (res && res.error) || { code: "unknown" },
          `${sentryFeaturePrefix}-rotate`,
          "Couldn't rotate that key. Please try again."
        );
        return;
      }
      if (!isValidCreateResponse(res)) {
        surfaceError(
          new Error("empty_response_body"),
          `${sentryFeaturePrefix}-rotate-empty-response`,
          "Key rotation returned an unexpected response. Please try again."
        );
        return;
      }
      analyticsTrack(`${analyticsEventPrefix}_rotated`, {
        accountId,
        tier
      });
      setRevealedKey({
        rawKey: res.rawKey,
        keyId: res.keyId,
        keyMasked: res.keyMasked
      });
      await refetchList();
    } finally {
      setIsMutating(false);
      setPendingAction(null);
    }
  };

  const onRevoke = async (keyId) => {
    setIsMutating(true);
    setPendingAction({ keyId, action: "revoke" });
    try {
      const res = await fetchOurApi({
        path: "/api-keys/revoke",
        method: "POST",
        data: { companyId: accountId, keyId }
      }).catch((err) => ({
        error: { code: "network", message: err && err.message }
      }));
      if (!res || res.error) {
        surfaceError(
          (res && res.error) || { code: "unknown" },
          `${sentryFeaturePrefix}-revoke`,
          "Couldn't revoke that key. Please try again."
        );
        return;
      }
      analyticsTrack(`${analyticsEventPrefix}_revoked`, {
        accountId,
        tier
      });
      await refetchList();
    } finally {
      setIsMutating(false);
      setPendingAction(null);
    }
  };

  const onCloseReveal = () => {
    if (revealedKey) {
      analyticsTrack(`${analyticsEventPrefix}_reveal_confirmed`, {
        accountId,
        tier
      });
    }
    setRevealedKey(null);
  };

  const listErrorContent = (
    <Grid item>
      <Card
        title={title}
        subtitle="Manage the API key your agent uses to reach Aclymate"
        icon={<FontAwesomeIcon icon={faKey} />}
      >
        <Box px={2} pb={2}>
          <Typography variant="body2" color="textSecondary">
            {listErrorCopy}
          </Typography>
        </Box>
      </Card>
    </Grid>
  );

  const revealPanel = revealedKey && (
    <Grid item>
      <KeyRevealPanel
        key={revealedKey.keyId}
        revealedKey={revealedKey}
        onConfirm={onCloseReveal}
      />
    </Grid>
  );

  if (listError) {
    return (
      <Grid container direction="column" spacing={2}>
        {revealPanel}
        {listErrorContent}
      </Grid>
    );
  }

  return (
    <Grid container direction="column" spacing={2}>
      {revealPanel}
      <Grid item>
        <Card
          isLoading={listLoading}
          title={title}
          subtitle="Manage the API key your agent uses to reach Aclymate"
          icon={<FontAwesomeIcon icon={faKey} />}
          action={
            <EulaStatusPill
              requiresAcceptance={requiresAcceptance}
              currentVersion={currentVersion}
              agreementNoun={agreementNoun}
              onClick={() =>
                setModalMode(requiresAcceptance ? "accept" : "review")
              }
            />
          }
        >
          <Box px={2} pb={2}>
            {!isAdmin && (
              <Stack spacing={2}>
                <Typography variant="body2" color="textSecondary">
                  Only admins can manage API keys.
                </Typography>
                {hasActiveKey &&
                  activeKeys.map((keyRow) => (
                    <Fragment key={keyRow.keyId}>
                      <ActiveKeyRow
                        apiKey={keyRow}
                        isAdmin={false}
                        isMutating={false}
                      />
                      {extraSlots?.afterActiveKeyRow?.({
                        keyId: keyRow.keyId,
                        keyMasked: keyRow.keyMasked,
                        tier: keyRow.tier,
                        mode: keyRow.mode,
                        createdAt: keyRow.createdAt,
                        isMutating: false,
                        pendingAction: null
                      })}
                    </Fragment>
                  ))}
                {!hasActiveKey && !listLoading && (
                  <Typography variant="body2" color="textSecondary">
                    No active key yet.
                  </Typography>
                )}
              </Stack>
            )}
            {isAdmin && (
              <Stack spacing={2}>
                {hasActiveKey &&
                  activeKeys.map((keyRow) => (
                    <Fragment key={keyRow.keyId}>
                      <ActiveKeyRow
                        apiKey={keyRow}
                        isAdmin
                        isMutating={isMutating}
                        pendingAction={pendingAction}
                        onRotate={onRotate}
                        onRevoke={onRevoke}
                      />
                      {extraSlots?.afterActiveKeyRow?.({
                        keyId: keyRow.keyId,
                        keyMasked: keyRow.keyMasked,
                        tier: keyRow.tier,
                        mode: keyRow.mode,
                        createdAt: keyRow.createdAt,
                        isMutating,
                        pendingAction
                      })}
                    </Fragment>
                  ))}
                {!hasActiveKey && !listLoading && (
                  <Typography variant="body2" color="textSecondary">
                    You don&apos;t have an active key yet. Generate one below.
                  </Typography>
                )}
                <Stack spacing={1} alignItems="flex-start">
                  <Stack direction="row" spacing={1}>
                    <LoadingButton
                      label={`Generate ${tierLabel} Key`}
                      color="primary"
                      onClick={onGenerate}
                      isLoading={isMutating && !revealedKey}
                      disabled={disableGenerate}
                    />
                    {requiresAcceptance && (
                      <LoadingButton
                        label={`Accept ${agreementNoun}`}
                        color="primary"
                        variant="outlined"
                        onClick={() => setModalMode("accept")}
                        isLoading={false}
                      />
                    )}
                  </Stack>
                  {requiresAcceptance && generateHelperText && (
                    <Typography variant="caption" color="textSecondary">
                      {generateHelperText}
                    </Typography>
                  )}
                  {!requiresAcceptance && hasActiveLiveKey && (
                    <Typography variant="caption" color="textSecondary">
                      You already have an active key. Rotate it above to
                      generate a new one.
                    </Typography>
                  )}
                </Stack>
              </Stack>
            )}
          </Box>
        </Card>
      </Grid>
      {isAdmin && hasActiveKey && (
        <Grid item>
          <ConnectionInstructionsTabs
            tier={tier}
            apiKeyMasked={activeKeys[0]?.keyMasked}
            analyticsEventPrefix={analyticsEventPrefix}
          />
        </Grid>
      )}
      {isAdmin && (
        <Grid item>
          <Card
            title="Audit log"
            subtitle="Every key your company has held in the last 90 days"
            icon={<FontAwesomeIcon icon={faKey} />}
          >
            <Box px={2} pb={2}>
              {revokedKeys.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                  No revoked keys yet.
                </Typography>
              )}
              {revokedKeys.length > 0 && (
                <Stack spacing={1}>
                  {revokedKeys.map((keyRow) => (
                    <AuditListRow key={keyRow.keyId} apiKey={keyRow} />
                  ))}
                </Stack>
              )}
            </Box>
          </Card>
        </Grid>
      )}
      {modalMode && (
        <EulaAcceptanceModal
          open
          mode={modalMode}
          tier={tier}
          accountId={accountId}
          eulaStatus={tierEulaStatus}
          analyticsEventPrefix={analyticsEventPrefix}
          activateSnackbar={activateSnackbar}
          onAccepted={() => {
            setModalMode(null);
            refetchList();
          }}
          onClose={() => setModalMode(null)}
        />
      )}
    </Grid>
  );
};

ApiKeyManagement.propTypes = {
  tier: PropTypes.oneOf(["tier-2", "tier-3"]).isRequired,
  accountId: PropTypes.string,
  isAdmin: PropTypes.bool,
  activateSnackbar: PropTypes.func,
  title: PropTypes.string.isRequired,
  analyticsEventPrefix: PropTypes.string,
  sentryFeaturePrefix: PropTypes.string,
  extraSlots: PropTypes.shape({
    afterActiveKeyRow: PropTypes.func
  })
};

export default ApiKeyManagement;
